import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs';
import { filter, switchMap, tap, catchError } from 'rxjs/operators';
import {
  DEFAULT_SUBSCRIPTION_PRICING,
  NabDirectPostPayload,
  NabTransactPaymentRequest,
  NabTransactPaymentResponse,
  ProjectSubscription,
  ProcessPaymentResponse,
  SubscriptionInvoice,
  SubscriptionPricingConfig,
  SubscriptionSeatUsage,
  SubscriptionStatus,
  SUBSCRIPTION_CONSTANTS
} from '../models/subscription.model';
import { NabTransactService } from './nab-transact.service';
import { TeamService } from './team.service';
import { BackendApiService } from './backend-api.service';
import { ProjectContextService } from './project-context.service';
import { ProjectSummary } from '../models/api-dto.model';

const STORAGE_SUB_KEY = 'sprinta_project_subscription';
const STORAGE_INV_KEY = 'sprinta_subscription_invoices';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  private pricingSubject: BehaviorSubject<SubscriptionPricingConfig>;
  public pricingConfig$: Observable<SubscriptionPricingConfig>;

  private subscriptionSubject: BehaviorSubject<ProjectSubscription>;
  public subscription$: Observable<ProjectSubscription>;

  private seatUsageSubject: BehaviorSubject<SubscriptionSeatUsage>;
  public seatUsage$: Observable<SubscriptionSeatUsage>;

  private invoicesSubject: BehaviorSubject<SubscriptionInvoice[]>;
  public invoices$: Observable<SubscriptionInvoice[]>;

  constructor(
    private teamService: TeamService,
    private nabService: NabTransactService,
    private http: HttpClient,
    private backendApi: BackendApiService,
    private projectContext: ProjectContextService
  ) {
    this.pricingSubject = new BehaviorSubject<SubscriptionPricingConfig>(DEFAULT_SUBSCRIPTION_PRICING);
    this.pricingConfig$ = this.pricingSubject.asObservable();
    this.loadPricingConfig();

    const initialSub = this.loadStoredSubscription();
    this.subscriptionSubject = new BehaviorSubject<ProjectSubscription>(initialSub);
    this.subscription$ = this.subscriptionSubject.asObservable();

    const initialInvoices = this.loadStoredInvoices();
    this.invoicesSubject = new BehaviorSubject<SubscriptionInvoice[]>(initialInvoices);
    this.invoices$ = this.invoicesSubject.asObservable();

    const initialUsage = this.computeSeatUsage(initialSub.units);
    this.seatUsageSubject = new BehaviorSubject<SubscriptionSeatUsage>(initialUsage);
    this.seatUsage$ = this.seatUsageSubject.asObservable();

    // Recompute seats whenever teams change
    this.teamService.currentTeam$.subscribe(() => {
      this.refreshSeatUsage();
    });

    // Synchronize with live backend API
    this.syncWithApi();
  }

  private syncWithApi(): void {
    this.projectContext.currentProject$.pipe(
      filter((p): p is ProjectSummary => !!p && !!p.id),
      switchMap(project => forkJoin({
        subDto: this.backendApi.getSubscription(project.id).pipe(catchError(() => of(null))),
        invoices: this.backendApi.getInvoices(project.id).pipe(catchError(() => of([])))
      }))
    ).subscribe(({ subDto, invoices }) => {
      if (subDto) {
        const sub = this.mapDtoToSubscription(subDto);
        this.subscriptionSubject.next(sub);
        this.saveSubscriptionToStorage(sub);
        this.refreshSeatUsage();
      }
      if (invoices && invoices.length > 0) {
        const mapped = invoices.map(inv => this.mapDtoToInvoice(inv));
        this.invoicesSubject.next(mapped);
        this.saveInvoicesToStorage(mapped);
      }
    });
  }

  private mapDtoToSubscription(dto: any): ProjectSubscription {
    const trialStart = dto.trialStartDate || dto.createdAt || new Date().toISOString();
    const trialEnd = dto.trialEndDate || new Date(Date.now() + 15 * 86400000).toISOString();
    const paidEnd = dto.paidPeriodEnd;
    const endStr = paidEnd || trialEnd;
    const daysRemaining = Math.max(0, Math.ceil((new Date(endStr).getTime() - Date.now()) / 86400000));
    return {
      id: dto.id || 'sub-project-primary',
      projectId: dto.projectId,
      projectReference: dto.projectId,
      status: dto.status as SubscriptionStatus,
      units: Number(dto.unitsPurchased) || 1,
      annualCost: (Number(dto.annualCostCents) || 72000) / 100,
      currency: dto.currency || 'AUD',
      trialStartDate: trialStart,
      trialEndDate: trialEnd,
      currentPeriodStart: dto.createdAt || new Date().toISOString(),
      currentPeriodEnd: endStr,
      gracePeriodEnd: dto.gracePeriodEnd || (dto.status === 'grace_period' ? endStr : ''),
      autoRenew: !!dto.autoRenew,
      daysRemaining
    };
  }

  private mapDtoToInvoice(dto: any): SubscriptionInvoice {
    return {
      invoiceNumber: dto.invoiceNumber || dto.id,
      date: dto.createdAt || dto.date || new Date().toISOString(),
      amount: (Number(dto.amountCents) || 0) / 100,
      units: Number(dto.unitsPurchased) || 1,
      status: dto.status || 'paid',
      nabReceipt: dto.nabReceipt || '',
      cardLast4: dto.cardLast4 || '0000',
      description: dto.description || 'Sprinta Project Licence'
    };
  }

  get pricing(): SubscriptionPricingConfig {
    return this.pricingSubject.value;
  }

  private loadPricingConfig(): void {
    this.http.get<SubscriptionPricingConfig>('assets/config/subscription-pricing.json').subscribe({
      next: (cfg) => {
        if (cfg && cfg.annualPriceAud) {
          this.pricingSubject.next(cfg);
          const current = this.subscriptionSubject?.value;
          if (current) {
            current.annualCost = current.units * cfg.annualPriceAud;
            this.subscriptionSubject.next(current);
            this.saveSubscriptionToStorage(current);
          }
          this.refreshSeatUsage();
        }
      },
      error: (err) => {
        console.warn('Could not load assets/config/subscription-pricing.json, using defaults', err);
      }
    });
  }

  getSubscription(): ProjectSubscription {
    return this.subscriptionSubject.value;
  }

  getSeatUsage(): SubscriptionSeatUsage {
    return this.seatUsageSubject.value;
  }

  getInvoices(): SubscriptionInvoice[] {
    return this.invoicesSubject.value;
  }

  isActionBlocked(): boolean {
    return this.subscriptionSubject.value.status === 'blocked';
  }

  public grantOnboardingGracePeriod(days = 15): void {
    const sub = this.subscriptionSubject.value;
    const now = new Date();
    const endDate = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);

    sub.status = 'grace_period';
    sub.currentPeriodEnd = endDate.toISOString();
    sub.gracePeriodEnd = endDate.toISOString();
    sub.daysRemaining = days;

    this.saveSubscriptionToStorage(sub);
    this.subscriptionSubject.next(sub);
    this.refreshSeatUsage();
  }

  refreshSeatUsage(): void {
    const currentSub = this.subscriptionSubject.value;
    const usage = this.computeSeatUsage(currentSub.units);
    this.seatUsageSubject.next(usage);
  }

  purchaseOrRenewDirectPost(units: number, payload: NabDirectPostPayload): Observable<ProcessPaymentResponse> {
    const totalAmount = units * this.pricing.annualPriceAud;
    payload.EPS_AMOUNT = totalAmount.toFixed(2);

    return this.nabService.processDirectPost(payload).pipe(
      tap(res => {
        if (res.confirmation) {
          const now = new Date();
          const periodEnd = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
          const graceEnd = new Date(periodEnd.getTime() + this.pricing.gracePeriodDays * 24 * 60 * 60 * 1000);
          const txId = res.details?.transactionId || ('NAB-' + Date.now());
          const last4 = res.details?.payment?.pan ? res.details.payment.pan.slice(-4) : '0000';

          const updated: ProjectSubscription = {
            ...this.subscriptionSubject.value,
            status: 'active',
            units,
            annualCost: totalAmount,
            currentPeriodStart: now.toISOString(),
            currentPeriodEnd: periodEnd.toISOString(),
            gracePeriodEnd: graceEnd.toISOString(),
            daysRemaining: 365,
            autoRenew: true,
            lastPaymentDate: now.toISOString(),
            lastPaymentReference: txId,
            paymentMethod: {
              cardBrand: this.nabService.detectCardBrand(payload.EPS_CARDNUMBER || ''),
              last4,
              expiryMonth: payload.EPS_EXPIRYMONTH,
              expiryYear: payload.EPS_EXPIRYYEAR,
              cardholderName: payload.EPS_CARDNAME
            }
          };

          const invoice: SubscriptionInvoice = {
            invoiceNumber: 'INV-' + now.getFullYear() + '-' + Math.floor(1000 + Math.random() * 9000),
            date: now.toISOString(),
            amount: totalAmount,
            units,
            status: 'paid',
            nabReceipt: txId,
            cardLast4: last4,
            description: `Annual Sprinta Subscription (${units} unit${units > 1 ? 's' : ''})`
          };

          const newInvoices = [invoice, ...this.invoicesSubject.value];
          this.invoicesSubject.next(newInvoices);
          this.saveInvoicesToStorage(newInvoices);

          this.subscriptionSubject.next(updated);
          this.saveSubscriptionToStorage(updated);
          this.refreshSeatUsage();
        }
      })
    );
  }

  purchaseOrRenew(units: number, req: NabTransactPaymentRequest): Observable<NabTransactPaymentResponse> {
    const totalAmount = units * this.pricing.annualPriceAud;
    req.amount = totalAmount;

    return this.nabService.processPayment(req).pipe(
      tap(res => {
        if (res.successful) {
          const now = new Date();
          const periodEnd = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
          const graceEnd = new Date(periodEnd.getTime() + this.pricing.gracePeriodDays * 24 * 60 * 60 * 1000);

          const updated: ProjectSubscription = {
            ...this.subscriptionSubject.value,
            status: 'active',
            units,
            annualCost: totalAmount,
            currentPeriodStart: now.toISOString(),
            currentPeriodEnd: periodEnd.toISOString(),
            gracePeriodEnd: graceEnd.toISOString(),
            daysRemaining: 365,
            autoRenew: true,
            lastPaymentDate: now.toISOString(),
            lastPaymentReference: res.receiptNumber,
            paymentMethod: {
              cardBrand: res.cardType || 'Credit Card',
              last4: res.last4 || '0000',
              expiryMonth: req.expiryMonth,
              expiryYear: req.expiryYear,
              cardholderName: req.cardholderName
            }
          };

          const invoice: SubscriptionInvoice = {
            invoiceNumber: 'INV-' + now.getFullYear() + '-' + Math.floor(1000 + Math.random() * 9000),
            date: now.toISOString(),
            amount: totalAmount,
            units,
            status: 'paid',
            nabReceipt: res.receiptNumber,
            cardLast4: res.last4 || '0000',
            description: `Annual Sprinta Subscription (${units} unit${units > 1 ? 's' : ''})`
          };

          const newInvoices = [invoice, ...this.invoicesSubject.value];
          this.invoicesSubject.next(newInvoices);
          this.saveInvoicesToStorage(newInvoices);

          this.subscriptionSubject.next(updated);
          this.saveSubscriptionToStorage(updated);
          this.refreshSeatUsage();
        }
      })
    );
  }

  adjustUnits(newUnits: number): Observable<ProjectSubscription> {
    const current = this.subscriptionSubject.value;
    const clampedUnits = Math.max(1, newUnits);
    const updated: ProjectSubscription = {
      ...current,
      units: clampedUnits,
      annualCost: clampedUnits * this.pricing.annualPriceAud
    };
    this.subscriptionSubject.next(updated);
    this.saveSubscriptionToStorage(updated);
    this.refreshSeatUsage();
    return of(updated);
  }

  resetSubscription(): void {
    try {
      localStorage.removeItem(STORAGE_SUB_KEY);
      localStorage.removeItem(STORAGE_INV_KEY);
    } catch {}
    const defaultSub = this.createDefaultSubscription();
    this.subscriptionSubject.next(defaultSub);
    this.invoicesSubject.next([]);
    this.refreshSeatUsage();
  }

  private computeSeatUsage(unitsPurchased: number): SubscriptionSeatUsage {
    const teams = this.teamService.getTeamsSnapshot();
    const activeMembers = new Map<string, string>();

    for (const t of teams) {
      for (const m of t.members) {
        if (m.status === 'active') {
          const key = (m.email || m.reference || m.displayName).toLowerCase().trim();
          activeMembers.set(key, m.projectRole || 'developer');
        }
      }
    }

    let devCount = 0;
    let stakeholderCount = 0;
    let ownerCount = 1;

    for (const [_, role] of activeMembers) {
      if (role === 'developer') devCount++;
      else if (role === 'internal_stakeholder' || role === 'external_stakeholder') stakeholderCount++;
      else if (role === 'owner') ownerCount++;
    }

    const seats = this.pricing.seatsPerUnit;
    const ownersCapacity = unitsPurchased * seats.owners;
    const developersCapacity = unitsPurchased * seats.developers;
    const stakeholdersCapacity = unitsPurchased * seats.stakeholders;

    const unitsForOwners = Math.ceil(ownerCount / seats.owners);
    const unitsForDevs = Math.ceil(devCount / seats.developers);
    const unitsForStakeholders = Math.ceil(stakeholderCount / seats.stakeholders);

    const unitsRequired = Math.max(1, unitsForOwners, unitsForDevs, unitsForStakeholders);
    const isOverCapacity = ownerCount > ownersCapacity || devCount > developersCapacity || stakeholderCount > stakeholdersCapacity;
    const extraUnitsNeeded = Math.max(0, unitsRequired - unitsPurchased);

    return {
      ownersUsed: ownerCount,
      developersUsed: devCount,
      stakeholdersUsed: stakeholderCount,
      ownersCapacity,
      developersCapacity,
      stakeholdersCapacity,
      unitsRequired,
      unitsPurchased,
      isOverCapacity,
      extraUnitsNeeded
    };
  }

  private createDefaultSubscription(): ProjectSubscription {
    const now = new Date();
    const trialEnd = new Date(now.getTime() + this.pricing.trialDays * 86400000);
    const graceEnd = new Date(trialEnd.getTime() + this.pricing.gracePeriodDays * 86400000);

    return {
      id: 'sub-project-primary',
      projectReference: 'sprinta-project',
      status: 'trial',
      units: 1,
      annualCost: this.pricing.annualPriceAud,
      currency: this.pricing.currency || 'AUD',
      trialStartDate: now.toISOString(),
      trialEndDate: trialEnd.toISOString(),
      currentPeriodStart: now.toISOString(),
      currentPeriodEnd: trialEnd.toISOString(),
      gracePeriodEnd: graceEnd.toISOString(),
      daysRemaining: this.pricing.trialDays,
      autoRenew: true
    };
  }

  private loadStoredSubscription(): ProjectSubscription {
    try {
      if (typeof localStorage !== 'undefined') {
        const item = localStorage.getItem(STORAGE_SUB_KEY);
        if (item) {
          const parsed = JSON.parse(item) as ProjectSubscription;
          if (parsed && (!parsed.annualCost || parsed.annualCost === 120 * parsed.units)) {
            parsed.annualCost = parsed.units * this.pricing.annualPriceAud;
          }
          return this.recalculateLifecycleStatus(parsed);
        }
      }
    } catch (e) {
      console.warn('Unable to load subscription from storage', e);
    }
    return this.createDefaultSubscription();
  }

  private recalculateLifecycleStatus(sub: ProjectSubscription): ProjectSubscription {
    const now = new Date();
    const isPaid = !!sub.lastPaymentDate;

    if (isPaid) {
      const end = new Date(sub.currentPeriodEnd);
      const daysRemaining = Math.ceil((end.getTime() - now.getTime()) / 86400000);
      sub.daysRemaining = daysRemaining;

      if (daysRemaining > this.pricing.renewalWarningDays) {
        sub.status = 'active';
      } else if (daysRemaining >= 0) {
        sub.status = 'expiring_soon';
      } else {
        const overdueDays = Math.abs(daysRemaining);
        sub.status = overdueDays <= this.pricing.gracePeriodDays ? 'grace_period' : 'blocked';
      }
    } else if (sub.trialEndDate) {
      const trialEnd = new Date(sub.trialEndDate);
      const daysRemaining = Math.ceil((trialEnd.getTime() - now.getTime()) / 86400000);
      sub.daysRemaining = daysRemaining;

      if (daysRemaining >= 0) {
        sub.status = 'trial';
      } else {
        const overdueDays = Math.abs(daysRemaining);
        sub.status = overdueDays <= this.pricing.gracePeriodDays ? 'grace_period' : 'blocked';
      }
    }

    return sub;
  }

  private loadStoredInvoices(): SubscriptionInvoice[] {
    try {
      if (typeof localStorage !== 'undefined') {
        const item = localStorage.getItem(STORAGE_INV_KEY);
        if (item) {
          return JSON.parse(item);
        }
      }
    } catch {}
    return [];
  }

  private saveSubscriptionToStorage(sub: ProjectSubscription): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_SUB_KEY, JSON.stringify(sub));
      }
    } catch {}
  }

  private saveInvoicesToStorage(invoices: SubscriptionInvoice[]): void {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_INV_KEY, JSON.stringify(invoices));
      }
    } catch {}
  }
}
